import type { Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { computed, unref } from 'vue'
import { getPublicApi } from './api'

export interface PublicTrackerPosition {
  timestamp: string
  address: string
  /** [lng, lat] */
  location: [number, number]
}

export interface PublicTracker {
  name: string
  current_position: PublicTrackerPosition | null
  positions: PublicTrackerPosition[]
  shareable_link_logo: { mode: 'custom' | 'none' | 'default'; url: string | null } | null
}

export interface GetPublicTrackerParams {
  date_from?: string
  date_to?: string
}

/**
 * Live view behind a public tracker share link. No session — the share uuid is
 * the only credential.
 *
 * A disabled link and an unknown uuid are indistinguishable by design, so any
 * failure is surfaced as one unavailable state; `retry: false` keeps that
 * immediate rather than retrying a uuid that will never resolve.
 */
export default (
  uuid: Ref<string> | string,
  params?: Ref<GetPublicTrackerParams> | GetPublicTrackerParams,
) => {
  return useQuery({
    queryKey: ['public-tracker', () => unref(uuid), () => unref(params)],
    queryFn: () => {
      const { api, baseURL } = getPublicApi()
      return api
        .get(`/api/public-trackers/${unref(uuid)}`, {
          baseURL,
          params: unref(params) ?? {},
        })
        .then((res) => res.data.data as PublicTracker)
    },
    enabled: computed(() => !!unref(uuid)),
    retry: false,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })
}
